/**
 * @Author: wuyang
 * @Date: 2024/1/2
 * @Description: ""
 */
import {
	Atom,
	createStore as createAtomStore,
	PrimitiveAtom,
	WritableAtom
} from 'jotai'
import { RESET } from 'jotai/utils'

interface IAtomListener<Value> {
	({ newValue, oldValue }: { newValue: Value; oldValue: Value }): void
}

interface ICreateStoreReturn {
	get: <Value>(atom: Atom<Value>) => Value
	set: <Value_1, Arg, Result>(
		atom: WritableAtom<Value_1, any, Result>,
		args: Arg,
		options?: {
			silent?: boolean
		}
	) => Result
	sub: <Value>(
		atom: Atom<Value>,
		listener: IAtomListener<Value>,
		options?: {
			immediate?: boolean
		}
	) => () => void
	del: <Value>(atom: WritableAtom<Value, any, any>) => Value
	getAtomStore: typeof createAtomStore
}

export interface IAtomMethod<T = unknown> {
	get: () => T
	set: (
		value: T | typeof RESET,
		options?: {
			silent?: boolean
		}
	) => void
	error: (
		value: {
			message?: string
			code?: number
		},
		options?: {
			silent?: boolean
		}
	) => void
	sub: (
		listener: IAtomListener<T>,
		options?: {
			immediate?: boolean
		}
	) => () => void
	del: () => T
	atom: () => PrimitiveAtom<T>
}

export const createStore = (): ICreateStoreReturn => {
	const store = createAtomStore()
	const listeners: Map<Atom<unknown>, Set<IAtomListener<unknown>>> = new Map()

	// 自定义获取
	const getAtom: ICreateStoreReturn['get'] = (atom) => {
		return store.get(atom)
	}

	// 自定义设置
	const setAtom: ICreateStoreReturn['set'] = (atom, arg, options) => {
		const oldValue = getAtom(atom)
		const result = store.set(atom, arg)
		const listenersSet = listeners.get(atom)
		if (!options?.silent && listenersSet) {
			listenersSet.forEach((lis) => {
				lis?.({
					newValue: arg,
					oldValue: oldValue
				})
			})
		}
		return result
	}

	// 删除
	const delAtom: ICreateStoreReturn['del'] = (atom) => {
		return setAtom(atom, undefined, { silent: true })
	}
	// 自定义监听
	const subAtom: ICreateStoreReturn['sub'] = (atom, listener, options) => {
		const oldValue = getAtom(atom)
		if (options?.immediate) {
			listener?.({
				newValue: oldValue,
				oldValue
			})
		}
		let listenersSet = listeners.get(atom) as Set<typeof listener>
		if (!listenersSet) {
			listenersSet = new Set() as Set<typeof listener>
			listeners.set(atom, listenersSet as any)
		}

		// 如果原来有当前的监听器，直接返回一个以前的就行
		if (!listenersSet.has(listener)) {
			listenersSet.add(listener)
		}
		return () => {
			listenersSet.delete(listener)
		}
	}

	return {
		get: getAtom,
		set: setAtom,
		sub: subAtom,
		del: delAtom,
		getAtomStore: () => store
	}
}
